import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lightbulb, Send, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { suggestionAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { toast } from 'sonner';

const CATEGORIES = ['general', 'feature', 'bug', 'ui_ux', 'content', 'other'];

export default function SuggestionsPage() {
	const { t } = useTranslation();
	const { user } = useAuth();
	const [category, setCategory] = useState('general');
	const [message, setMessage] = useState('');
	const [email, setEmail] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [sent, setSent] = useState(false);

	useDocumentMeta({
		title: `${t('suggestions.title')} | Equi-Market`,
		description: t('suggestions.subtitle'),
	});

	const onSubmit = async (e) => {
		e.preventDefault();
		if (message.trim().length < 10) {
			toast.error(t('suggestions.error_too_short'));
			return;
		}
		setSubmitting(true);
		try {
			const payload = { category, message: message.trim() };
			if (!user && email.trim()) payload.email = email.trim();
			await suggestionAPI.submit(payload);
			setSent(true);
			setMessage('');
			setEmail('');
			setCategory('general');
			toast.success(t('suggestions.success'));
		} catch (err) {
			toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || t('common.error'));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2]" data-testid="suggestions-page">
			{/* Header */}
			<div className="bg-white border-b border-[#E6E2D6]">
				<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
					<div className="flex items-center gap-3 mb-2">
						<Lightbulb className="w-6 h-6 text-[#8B4513]" />
						<h1 className="text-3xl md:text-4xl font-medium text-[#1A3C34] font-['Playfair_Display']">
							{t('suggestions.title')}
						</h1>
					</div>
					<p className="text-sm text-[#666055] max-w-2xl">{t('suggestions.subtitle')}</p>
				</div>
			</div>

			{/* Body */}
			<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
				{sent ? (
					<div
						className="bg-white border border-[#E6E2D6] rounded-sm p-10 text-center"
						data-testid="suggestions-thanks"
					>
						<div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#E6F0EC] mb-4">
							<Check className="w-6 h-6 text-[#1A3C34]" />
						</div>
						<h2 className="text-xl font-medium text-[#1A3C34] font-['Playfair_Display'] mb-2">
							{t('suggestions.thanks_title')}
						</h2>
						<p className="text-sm text-[#666055] mb-6 max-w-md mx-auto">
							{t('suggestions.thanks_body')}
						</p>
						<Button
							onClick={() => setSent(false)}
							variant="outline"
							className="border-[#E6E2D6] text-[#1A3C34] rounded-sm"
							data-testid="suggestions-submit-another-btn"
						>
							{t('suggestions.submit_another')}
						</Button>
					</div>
				) : (
					<form
						onSubmit={onSubmit}
						className="bg-white border border-[#E6E2D6] rounded-sm p-6 sm:p-8 space-y-5"
						data-testid="suggestions-form"
					>
						{/* Category */}
						<div>
							<label className="text-xs font-semibold text-[#666055] uppercase tracking-wider block mb-2">
								{t('suggestions.category')}
							</label>
							<Select value={category} onValueChange={setCategory}>
								<SelectTrigger
									className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
									data-testid="suggestions-category-trigger"
								>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{CATEGORIES.map((c) => (
										<SelectItem key={c} value={c} data-testid={`suggestions-category-${c}`}>
											{t(`suggestions.cat_${c}`)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* Message */}
						<div>
							<label className="text-xs font-semibold text-[#666055] uppercase tracking-wider block mb-2">
								{t('suggestions.your_idea')}
							</label>
							<Textarea
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								placeholder={t('suggestions.message_placeholder')}
								maxLength={4000}
								rows={8}
								className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm text-sm"
								data-testid="suggestions-message-input"
								required
							/>
							<div className="text-[11px] text-[#666055]/70 mt-1 text-right">{message.length} / 4000</div>
						</div>

						{/* Optional email for anonymous users */}
						{!user && (
							<div>
								<label className="text-xs font-semibold text-[#666055] uppercase tracking-wider block mb-2">
									{t('suggestions.email_optional')}
								</label>
								<Input
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="you@example.com"
									maxLength={200}
									className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
									data-testid="suggestions-email-input"
								/>
								<p className="text-[11px] text-[#666055]/70 mt-1">
									{t('suggestions.email_help')}
								</p>
							</div>
						)}

						{user && (
							<div className="text-xs text-[#666055] bg-[#F2F0E9] border border-[#E6E2D6] rounded-sm p-3" data-testid="suggestions-signedin-note">
								{t('suggestions.signed_in_as', { name: user.full_name || user.email })}
							</div>
						)}

						<div className="flex justify-end pt-2 border-t border-[#E6E2D6]">
							<Button
								type="submit"
								disabled={submitting || message.trim().length < 10}
								className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-11 px-6"
								data-testid="suggestions-submit-btn"
							>
								<Send className="w-4 h-4 mr-2" />
								{submitting ? t('suggestions.sending') : t('suggestions.submit')}
							</Button>
						</div>
					</form>
				)}
			</div>
		</div>
	);
}